import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Shield, UserPlus } from 'lucide-react';
import CreateUserModal from '@/components/CreateUserModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ['admin', 'member', 'viewer'];

export default function UsersManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user, fetchUsers]);
  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Role updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Shield className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <p className="text-text-primary font-manrope text-xl">Admin access required</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-primary font-manrope text-xl">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">
            Team Members
          </h1>
          <p className="text-text-secondary mt-2">
            Manage roles and permissions for your organization
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="btn-create-user"
        >
          <UserPlus size={20} />
          Create User
        </button>
      </div>

      <div className="bg-white border-2 border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-4 text-text-primary font-medium">{u.full_name}</td>
                  <td className="px-6 py-4 text-text-secondary">{u.email}</td>
                  <td className="px-6 py-4">
                    {u.id === user.id ? (
                      <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium uppercase">
                        {u.role} (You)
                      </span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        className="bg-white border-2 border-gray-200 px-3 py-1 text-sm focus:border-primary focus:outline-none"
                        data-testid={`select-role-${u.id}`}
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-6 py-4 text-text-secondary text-sm">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-white border border-gray-200 p-6">
        <h3 className="font-manrope font-semibold text-lg text-text-primary mb-4">Role Permissions</h3>
        <div className="space-y-3 text-sm text-text-secondary">
          <div>
            <span className="font-medium text-text-primary">Admin:</span> Full access - can manage credentials, share with team, and manage user roles
          </div>
          <div>
            <span className="font-medium text-text-primary">Member:</span> Can create, edit, and share credentials they own or have access to
          </div>
          <div>
            <span className="font-medium text-text-primary">Viewer:</span> Read-only access to shared credentials
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
}